import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { api, API_BASE_PREFIX } from "../utils/api";
import { setAuthSnapshot, clearAuthSnapshot, clearAuthCookies } from "../utils/authutils";
import { dropCachedQueriesOnSessionChange } from "../utils/queryClient";

export const AuthContext = createContext();

const EMPTY_USER = {
  userId: null,
  user_type: null,
  platform_type: null,
  is_dependent: false,
  name: null,
  first_name: null,
  last_name: null,
  email: null,
  phone_no: null,
  is_profile_completed: null,
  profile_image: null,
};

const normalizeProfile = (data = {}) => ({
  userId: data.user_id ?? data.userId ?? null,
  user_type: data.user_type ?? null,
  platform_type: data.platform_type ?? data.role?.platform_type ?? null,
  is_dependent: data.is_dependent ?? data.is_dependant ?? false,
  first_name: data.first_name ?? null,
  last_name: data.last_name ?? null,
  name: data.name ?? ([data.first_name, data.last_name].filter(Boolean).join(" ") || null),
  email: data.email ?? null,
  phone_no: data.phone_no ?? null,
  is_profile_completed: data.is_profile_completed ?? null,
  profile_image: data.profile_image ?? null,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(EMPTY_USER);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Set once a user-initiated login/logout happens, so a late boot-time /me
  // result can't clobber it (the probe and the action can race at startup).
  const userActionRef = useRef(false);

  const applyProfile = useCallback((data) => {
    const normalized = normalizeProfile(data);
    setUser(normalized);
    setIsAuthenticated(true);
    setAuthSnapshot(normalized);
    return normalized;
  }, []);

  const clearAuthState = useCallback(() => {
    setUser(EMPTY_USER);
    setIsAuthenticated(false);
    clearAuthSnapshot();
    dropCachedQueriesOnSessionChange();
  }, []);

  useEffect(() => {
    clearAuthCookies();
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`${API_BASE_PREFIX}/me/`, { _silentAuth: true });
        if (!cancelled && !userActionRef.current) applyProfile(res?.data?.data || {});
      } catch (_) {
        if (!cancelled && !userActionRef.current) clearAuthState();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyProfile, clearAuthState]);

  const login = useCallback(
    (profile) => {
      userActionRef.current = true;
      dropCachedQueriesOnSessionChange();
      return applyProfile(profile);
    },
    [applyProfile],
  );

  const logout = useCallback(async () => {
    userActionRef.current = true;
    try {
      await api.post(`${API_BASE_PREFIX}/logout/`);
    } catch (_) {
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const updateProfile = useCallback((profile) => {
    setUser((prev) => {
      const merged = normalizeProfile({ ...prev, ...profile });
      setAuthSnapshot(merged);
      return merged;
    });
    setIsAuthenticated(true);
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, user, login, logout, updateProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};
